import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  Timestamp,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  setDoc,
  where,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { useAuth } from '../AuthContext';
import { buildPdfPayloadFromTemplate } from '../office/pdfPayloadBuilder';
import { db, storage } from '../firebase';

type BrokerFilterOption = {
  key: string;
  label: string;
  type: 'all' | 'broker' | 'agency';
  brokerIds?: string[];
  agencyBrokerIds?: string[];
  agencyName?: string | null;
  brokerName?: string | null;
  phoneE164?: string | null;
  email?: string | null;
  isAgencyHead?: boolean;
  searchText?: string;
};

type BulkBrokerReportRow = {
  filter: BrokerFilterOption;
  leadCount: number;
  status: 'ready' | 'testing' | 'test_sent' | 'sending' | 'sent' | 'failed' | 'skipped';
  message?: string;
};

type WhatsappTemplateOption = {
  id: string;
  label?: string | null;
  name?: string | null;
  language?: string | null;
  headerFormat?: string | null;
  bodyText?: string | null;
  variables?: Array<{
    key?: string | null;
    label?: string | null;
    source?: string | null;
    defaultValue?: string | null;
  }>;
  buttonVariables?: Array<{
    index?: string | number | null;
    key?: string | null;
    label?: string | null;
    source?: string | null;
    defaultValue?: string | null;
  }>;
};

type LeadRow = {
  id: string;
  name?: string | null;
  phone?: string | null;
  phoneE164?: string | null;
  projectName?: string | null;
  status?: string | null;
  createdAt?: any;
  referredBrokerId?: string | null;
  referredBrokerName?: string | null;
  referredAgencyName?: string | null;
  closedAt?: any;
  closeReason?: string | null;
  closePrimaryReason?: string | null;
  closeRequiredType?: string | null;
  closedAnalysis?: {
    primaryReason?: string | null;
    primaryReasonBase?: string | null;
    requiredType?: string | null;
  } | null;
};

const formatWhatsappPersonName = (value: string): string =>
  String(value || '')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

type LeadStatusRow = {
  id: string;
  label?: string | null;
  name?: string | null;
};

type LastActivitySummary = {
  label: string;
  at: unknown;
};

type ReportMode = 'brokerAgency' | 'typeRequired';

function normalizeText(value: unknown) {
  return String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

function normalizeSearchText(value: unknown) {
  return normalizeText(value).replace(/[^a-z0-9\s]/g, ' ');
}

function toDateOnlyInput(value: Date) {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseDateInput(value: string, endOfDay = false) {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  if (endOfDay) parsed.setHours(23, 59, 59, 999);
  return parsed;
}

function formatDateTime(value: unknown) {
  const maybe = value && typeof (value as any)?.toDate === 'function'
    ? (value as any).toDate()
    : value instanceof Date
      ? value
      : typeof value === 'string' || typeof value === 'number'
        ? new Date(value)
        : null;
  if (!(maybe instanceof Date) || Number.isNaN(maybe.getTime())) return '—';
  return maybe.toLocaleDateString('en-GB');
}

function formatLeadName(value: unknown) {
  const raw = String(value || '')
    .trim()
    .replace(/\s+/g, ' ');
  if (!raw) return '—';
  return raw.replace(/\b\w+/g, (part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase());
}

function formatPhone(row: LeadRow) {
  const raw = String(row.phone || row.phoneE164 || '')
    .trim()
    .replace(/\s+/g, '')
    .replace(/[^\d+]/g, '');
  if (!raw) return '—';

  const digits = raw.replace(/\D/g, '');
  const localDigits = digits.length > 10 ? digits.slice(-10) : digits;
  if (!localDigits) return '—';
  if (localDigits.length <= 5) return localDigits;
  return `${localDigits.slice(0, 5)} ${localDigits.slice(5)}`;
}

function formatActivityLabel(value: any) {
  const typeRaw = String(value?.type || '').trim().toLowerCase();
  const eventRaw = String(value?.event || '').trim().toLowerCase();

  if (typeRaw === 'call') return 'Call';
  if (typeRaw === 'visit') return 'Visit';
  if (eventRaw === 'assign') return 'Assign';
  if (eventRaw === 'rating') return 'Rating';
  if (eventRaw === 'whatsapp-sent') return 'WhatsApp';
  if (eventRaw === 'appointment-scheduled') return 'Appointment Scheduled';
  if (eventRaw === 'appointment-completed') return 'Appointment Completed';
  if (eventRaw === 'appointment-rescheduled') return 'Appointment Rescheduled';
  if (typeRaw === 'internal' && eventRaw) {
    return eventRaw
      .split(/[-_\s]+/)
      .filter(Boolean)
      .map((part: string) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }
  if (typeRaw) return typeRaw.charAt(0).toUpperCase() + typeRaw.slice(1);
  return '—';
}

function formatLastActivity(summary: LastActivitySummary | null | undefined) {
  if (!summary) return '—';
  const dateText = formatDateTime(summary.at);
  if (!summary.label || summary.label === '—') return dateText;
  return dateText === '—' ? summary.label : `${summary.label} • ${dateText}`;
}

function toMillisLoose(value: unknown) {
  if (value && typeof (value as any)?.toMillis === 'function') {
    return Number((value as any).toMillis() || 0);
  }
  if (value && typeof (value as any)?.toDate === 'function') {
    const date = (value as any).toDate();
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
  }
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? 0 : value.getTime();
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
  }
  return 0;
}

function sanitizeFileNamePart(value: unknown) {
  return String(value || '')
    .trim()
    .replace(/[<>:"/\\|?*\u0000-\u001F]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function escapeHtml(value: unknown) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function blobFromRenderTemplateResult(resultData: unknown): Blob {
  const data = (resultData || {}) as {
    pdfBytes?: number[] | Record<string, number>;
    pdfBase64?: string | { type?: string; data?: number[] };
    mimeType?: string;
  };
  const mimeType = data.mimeType || 'application/pdf';
  const rawBytes = Array.isArray(data.pdfBytes)
    ? data.pdfBytes
    : data.pdfBytes && typeof data.pdfBytes === 'object'
      ? Object.keys(data.pdfBytes)
          .filter((key) => /^\d+$/.test(key))
          .sort((a, b) => Number(a) - Number(b))
          .map((key) => Number((data.pdfBytes as Record<string, number>)[key] ?? 0))
      : null;
  if (rawBytes && rawBytes.length > 0) {
    return new Blob([new Uint8Array(rawBytes)], { type: mimeType });
  }
  const rawBase64 =
    typeof data.pdfBase64 === 'string'
      ? data.pdfBase64
      : data.pdfBase64?.type === 'Buffer' && Array.isArray(data.pdfBase64?.data)
        ? (() => {
            const source = data.pdfBase64?.data || [];
            let binaryBuffer = '';
            const chunkSize = 0x8000;
            for (let i = 0; i < source.length; i += chunkSize) {
              const chunk = source.slice(i, i + chunkSize);
              binaryBuffer += String.fromCharCode(...chunk);
            }
            return btoa(binaryBuffer);
          })()
        : '';
  if (!rawBase64) throw new Error('PDF response was empty.');
  const normalizedBase64 = String(rawBase64)
    .replace(/^data:application\/pdf;base64,/i, '')
    .replace(/\s+/g, '')
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const paddedBase64 = normalizedBase64.padEnd(
    normalizedBase64.length + ((4 - (normalizedBase64.length % 4)) % 4),
    '='
  );
  const binary = atob(paddedBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mimeType });
}

async function blobToUint8Array(blob: Blob) {
  return new Uint8Array(await blob.arrayBuffer());
}

function matchesFilter(row: LeadRow, filter: BrokerFilterOption | null) {
  if (!filter || filter.type === 'all') return true;
  if (filter.type === 'broker') {
    const rowBrokerId = String(row.referredBrokerId || '').trim();
    if (rowBrokerId && Array.isArray(filter.brokerIds) && filter.brokerIds.length > 0) {
      return filter.brokerIds.includes(rowBrokerId);
    }
    return (
      normalizeText(row.referredBrokerName) === normalizeText(filter.brokerName) &&
      normalizeText(row.referredAgencyName) === normalizeText(filter.agencyName)
    );
  }
  return normalizeText(row.referredAgencyName) === normalizeText(filter.agencyName);
}

function matchesBrokerReportFilter(row: LeadRow, filter: BrokerFilterOption | null) {
  if (!filter || filter.type === 'all') return true;
  if (filter.type === 'broker' && filter.isAgencyHead && filter.agencyName) {
    const agencyNorm = normalizeText(filter.agencyName);
    const agencyBrokerIds = new Set((filter.agencyBrokerIds || []).map((id) => String(id || '').trim()).filter(Boolean));
    const rowBrokerId = String(row.referredBrokerId || '').trim();
    if (rowBrokerId && agencyBrokerIds.has(rowBrokerId)) return true;
    return !!agencyNorm && normalizeText(row.referredAgencyName) === agencyNorm;
  }
  return matchesFilter(row, filter);
}

function isNeedAnotherTypePrimaryReason(row: LeadRow) {
  const values = [
    row?.closePrimaryReason,
    row?.closeReason,
    row?.closedAnalysis?.primaryReason,
    row?.closedAnalysis?.primaryReasonBase,
  ]
    .map((v) => normalizeText(v))
    .filter(Boolean);
  return values.some((v) =>
    v === 'need another type' ||
    v === 'need other type'
  );
}

function requiredTypeForLead(row: LeadRow) {
  return String(
    row?.closeRequiredType ||
    row?.closedAnalysis?.requiredType ||
    ''
  )
    .trim()
    .replace(/\s+/g, ' ');
}

function isClosedLeadLike(row: LeadRow) {
  const statusNorm = normalizeText(row?.status);
  if (statusNorm === 'closed' || statusNorm === 'completed') return true;
  if (row?.closedAt) return true;
  if (String(row?.closeReason || '').trim()) return true;
  return false;
}

async function loadLeadRows(from: Date | null, to: Date | null) {
  const out: LeadRow[] = [];
  let cursor: any = null;
  const batchSize = 500;

  for (;;) {
    const parts: any[] = [];
    if (from) parts.push(where('createdAt', '>=', Timestamp.fromDate(from)));
    if (to) parts.push(where('createdAt', '<=', Timestamp.fromDate(to)));
    parts.push(orderBy('createdAt', 'desc'));
    parts.push(limit(batchSize));
    if (cursor) parts.push(startAfter(cursor));

    const snap = await getDocs(query(collection(db, 'leads'), ...parts));
    if (snap.empty) break;

    snap.docs.forEach((docSnap) => {
      out.push({ id: docSnap.id, ...(docSnap.data() as any) });
    });

    if (snap.docs.length < batchSize) break;
    cursor = snap.docs[snap.docs.length - 1];
  }

  return out;
}

export default function ReportsPage() {
  const { currentProjectId } = useAuth();
  const today = useMemo(() => new Date(), []);
  const startOfCurrentMonth = useMemo(() => {
    const next = new Date();
    next.setDate(1);
    next.setHours(0, 0, 0, 0);
    return next;
  }, []);

  const [periodMode, setPeriodMode] = useState<'all' | 'range'>('range');
  const [fromDate, setFromDate] = useState(toDateOnlyInput(startOfCurrentMonth));
  const [toDate, setToDate] = useState(toDateOnlyInput(today));
  const [reportMode, setReportMode] = useState<ReportMode>('brokerAgency');
  const [selectedFilter, setSelectedFilter] = useState<BrokerFilterOption | null>(null);
  const [filterOptions, setFilterOptions] = useState<BrokerFilterOption[]>([
    { key: 'all', label: 'All', type: 'all' },
  ]);
  const [leadStatuses, setLeadStatuses] = useState<LeadStatusRow[]>([]);
  const [selectedStatusKeys, setSelectedStatusKeys] = useState<string[]>([]);
  const [selectedRequiredType, setSelectedRequiredType] = useState<string>('all');
  const [requiredTypeOptions, setRequiredTypeOptions] = useState<string[]>([]);
  const [rows, setRows] = useState<LeadRow[]>([]);
  const [brokerAgencyCounts, setBrokerAgencyCounts] = useState<Record<string, number>>({});
  const [lastActivityByLeadId, setLastActivityByLeadId] = useState<Record<string, LastActivitySummary>>({});
  const [projectMeta, setProjectMeta] = useState<any | null>(null);
  const [companyMeta, setCompanyMeta] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [exportPdfBusy, setExportPdfBusy] = useState(false);
  const [exportPdfError, setExportPdfError] = useState<string | null>(null);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState<BulkBrokerReportRow[]>([]);
  const [bulkSelectedKeys, setBulkSelectedKeys] = useState<string[]>([]);
  const [bulkPreparing, setBulkPreparing] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkTestPhone, setBulkTestPhone] = useState('');
  const [bulkTestBrokerKey, setBulkTestBrokerKey] = useState('');
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [whatsappTemplates, setWhatsappTemplates] = useState<WhatsappTemplateOption[]>([]);
  const [whatsappTemplatesLoading, setWhatsappTemplatesLoading] = useState(false);
  const [bulkTemplateId, setBulkTemplateId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const statusOptions = useMemo(
    () =>
      leadStatuses
        .map((status) => {
          const key = String(status.id || '').trim();
          const label = String(status.label || status.name || status.id || '').trim();
          return key && label ? { key, label } : null;
        })
        .filter((value): value is { key: string; label: string } => Boolean(value))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [leadStatuses]
  );
  const selectedStatusSet = useMemo(
    () => new Set(selectedStatusKeys.map((value) => String(value).trim().toLowerCase()).filter(Boolean)),
    [selectedStatusKeys]
  );

  useEffect(() => {
    setSelectedRequiredType('all');
    if (reportMode === 'typeRequired') setSelectedStatusKeys([]);
  }, [reportMode]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const snap = await getDocs(query(collection(db, 'brokers'), orderBy('displayNameLower', 'asc')));
        if (cancelled) return;

        const next = new Map<string, BrokerFilterOption>();
        next.set('all', { key: 'all', label: 'All', type: 'all' });
        const agencyBrokerIdsByNorm = new Map<string, string[]>();

        snap.docs.forEach((docSnap) => {
          const row = docSnap.data() as any;
          const brokerId = String(docSnap.id || '').trim();
          const agencyName = String(row?.agencyName || '').trim();
          const agencyNorm = normalizeText(agencyName);
          if (!brokerId || !agencyNorm) return;
          agencyBrokerIdsByNorm.set(agencyNorm, Array.from(new Set([...(agencyBrokerIdsByNorm.get(agencyNorm) || []), brokerId])));
        });

        snap.docs.forEach((docSnap) => {
          const row = docSnap.data() as any;
          const brokerId = String(docSnap.id || '').trim();
          const brokerName = String(row?.brokerName || '').trim();
          const agencyName = String(row?.agencyName || '').trim();
          const agencyNorm = normalizeText(agencyName);
          const phoneE164 = String(row?.phoneE164 || '').trim();
          const email = String(row?.email || '').trim();
          const isAgencyHead = row?.isAgencyHead === true;

          if (agencyName) {
            const agencyKey = `agency:${agencyNorm}`;
            if (!next.has(agencyKey)) {
              next.set(agencyKey, {
                key: agencyKey,
                label: agencyName,
                type: 'agency',
                agencyName,
                agencyBrokerIds: agencyBrokerIdsByNorm.get(agencyNorm) || [],
                searchText: normalizeSearchText(agencyName),
              });
            }
          }

          if (brokerName) {
            const label = agencyName ? `${brokerName} (${agencyName})` : brokerName;
            const brokerKey = `broker:${normalizeText(brokerName)}:${agencyNorm}`;
            const existing = next.get(brokerKey);
            if (existing && existing.type === 'broker') {
              existing.brokerIds = Array.from(new Set([...(existing.brokerIds || []), brokerId]));
              existing.agencyBrokerIds = agencyBrokerIdsByNorm.get(agencyNorm) || existing.agencyBrokerIds || [];
              existing.phoneE164 = existing.phoneE164 || phoneE164 || null;
              existing.email = existing.email || email || null;
              existing.isAgencyHead = existing.isAgencyHead || isAgencyHead;
              existing.searchText = normalizeSearchText(
                `${existing.label} ${existing.brokerName || ''} ${existing.agencyName || ''}`
              );
            } else {
              next.set(brokerKey, {
                key: brokerKey,
                label,
                type: 'broker',
                brokerIds: brokerId ? [brokerId] : [],
                agencyBrokerIds: agencyBrokerIdsByNorm.get(agencyNorm) || [],
                agencyName: agencyName || null,
                brokerName,
                phoneE164: phoneE164 || null,
                email: email || null,
                isAgencyHead,
                searchText: normalizeSearchText(`${label} ${brokerName} ${agencyName}`),
              });
            }
          }
        });

        setFilterOptions(
          Array.from(next.values()).sort((a, b) => {
            if (a.type === 'all') return -1;
            if (b.type === 'all') return 1;
            if (a.type !== b.type) return a.type === 'agency' ? -1 : 1;
            return a.label.localeCompare(b.label);
          })
        );
        setSelectedFilter((prev) => {
          if (!prev) return next.get('all') || null;
          return next.get(prev.key) || next.get('all') || null;
        });
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.message || 'Failed to load broker filters.');
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setWhatsappTemplatesLoading(true);
      try {
        const snap = await getDocs(query(collection(db, 'whatsappTemplates'), orderBy('updatedAt', 'desc')));
        if (cancelled) return;
        setWhatsappTemplates(snap.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() as any) })));
      } catch (err: any) {
        if (cancelled) return;
        setBulkError(err?.message || 'Failed to load WhatsApp templates.');
        setWhatsappTemplates([]);
      } finally {
        if (!cancelled) setWhatsappTemplatesLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadProjectAndCompany = async () => {
      if (!currentProjectId) {
        setProjectMeta(null);
        setCompanyMeta(null);
        return;
      }
      try {
        const projectSnap = await getDoc(doc(db, 'projects', currentProjectId));
        const projectData = projectSnap.exists() ? projectSnap.data() : null;
        if (cancelled) return;
        setProjectMeta(projectData);
        const companyProfileId = String(projectData?.companyProfileId || '').trim();
        if (companyProfileId) {
          const companySnap = await getDoc(doc(db, 'companyProfiles', companyProfileId));
          if (cancelled) return;
          setCompanyMeta(companySnap.exists() ? companySnap.data() : null);
          return;
        }
        const fallbackCompanySnap = await getDocs(query(collection(db, 'companyProfiles'), limit(1)));
        if (cancelled) return;
        setCompanyMeta(fallbackCompanySnap.docs[0]?.data() || null);
      } catch {
        if (cancelled) return;
        setProjectMeta(null);
        setCompanyMeta(null);
      }
    };

    loadProjectAndCompany();
    return () => {
      cancelled = true;
    };
  }, [currentProjectId]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const snap = await getDocs(query(collection(db, 'leadStatuses'), orderBy('order', 'asc')));
        if (cancelled) return;
        setLeadStatuses(snap.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() as any) })));
      } catch {
        if (cancelled) return;
        setLeadStatuses([]);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let active = true;

    const refreshReport = async () => {
      setLoading(true);
      setError(null);
      try {
        const from = periodMode === 'range' ? parseDateInput(fromDate, false) : null;
        const to = periodMode === 'range' ? parseDateInput(toDate, true) : null;

        if (periodMode === 'range' && (!from || !to)) {
          throw new Error('Select a valid from date and to date.');
        }
        if (from && to && from.getTime() > to.getTime()) {
          throw new Error('From date must be before To date.');
        }

        const loaded = await loadLeadRows(from, to);
        if (!active) return;
        const reportBaseRows =
          reportMode === 'typeRequired'
            ? loaded.filter((row) => isClosedLeadLike(row) && isNeedAnotherTypePrimaryReason(row))
            : loaded;
        const agencyCountMap: Record<string, number> = {};
        const brokerIdCountMap: Record<string, number> = {};
        const brokerNameAgencyCountMap: Record<string, number> = {};
        reportBaseRows.forEach((row) => {
          const agencyNorm = normalizeText(row.referredAgencyName);
          if (agencyNorm) {
            agencyCountMap[agencyNorm] = Number(agencyCountMap[agencyNorm] || 0) + 1;
          }
          const brokerId = String(row.referredBrokerId || '').trim();
          if (brokerId) {
            brokerIdCountMap[brokerId] = Number(brokerIdCountMap[brokerId] || 0) + 1;
          }
          const brokerNorm = normalizeText(row.referredBrokerName);
          if (brokerNorm) {
            const key = `broker:${brokerNorm}:${agencyNorm}`;
            brokerNameAgencyCountMap[key] = Number(brokerNameAgencyCountMap[key] || 0) + 1;
          }
        });
        const nextCounts: Record<string, number> = {};
        filterOptions.forEach((option) => {
          if (option.type === 'all') {
            nextCounts[option.key] = reportBaseRows.length;
            return;
          }
          if (option.type === 'agency') {
            nextCounts[option.key] = Number(agencyCountMap[normalizeText(option.agencyName)] || 0);
            return;
          }
          const ids = Array.isArray(option.brokerIds) ? option.brokerIds : [];
          if (ids.length > 0) {
            const uniqIds = Array.from(new Set(ids.map((id) => String(id || '').trim()).filter(Boolean)));
            nextCounts[option.key] = uniqIds.reduce(
              (sum, id) => sum + Number(brokerIdCountMap[id] || 0),
              0
            );
            return;
          }
          const fallbackKey = `broker:${normalizeText(option.brokerName)}:${normalizeText(option.agencyName)}`;
          nextCounts[option.key] = Number(brokerNameAgencyCountMap[fallbackKey] || 0);
        });
        setBrokerAgencyCounts(nextCounts);
        const requiredTypes = Array.from(
          new Set(
            reportBaseRows
              .map((row) => requiredTypeForLead(row))
              .filter((v) => !!v)
          )
        ).sort((a, b) => a.localeCompare(b));
        setRequiredTypeOptions(requiredTypes);

        const filtered = reportBaseRows
          .filter((row) => (reportMode === 'brokerAgency' ? matchesBrokerReportFilter(row, selectedFilter) : true))
          .filter((row) =>
            reportMode === 'brokerAgency'
              ? (() => {
                  if (selectedStatusSet.size === 0) return true;
                  const raw = String(row.status || '').trim().toLowerCase();
                  if (!raw) return false;
                  return selectedStatusSet.has(raw);
                })()
              : selectedRequiredType === 'all'
                ? true
                : normalizeText(requiredTypeForLead(row)) === normalizeText(selectedRequiredType)
          )
          .sort((a, b) => {
            const aTime = typeof a?.createdAt?.toMillis === 'function' ? a.createdAt.toMillis() : 0;
            const bTime = typeof b?.createdAt?.toMillis === 'function' ? b.createdAt.toMillis() : 0;
            return bTime - aTime;
          });
        setRows(filtered);
      } catch (err: any) {
        if (!active) return;
        setRows([]);
        setError(err?.message || 'Failed to generate report.');
      } finally {
        if (active) setLoading(false);
      }
    };

    refreshReport();
    return () => {
      active = false;
    };
  }, [
    filterOptions,
    fromDate,
    periodMode,
    reportMode,
    selectedFilter,
    selectedRequiredType,
    selectedStatusSet,
    toDate,
  ]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const leadIds = rows
        .map((row) => String(row.id || '').trim())
        .filter(Boolean)
        .filter((id) => !lastActivityByLeadId[id]);
      if (leadIds.length === 0) return;

      const updates: Record<string, LastActivitySummary> = {};
      const batchSize = 25;

      for (let i = 0; i < leadIds.length; i += batchSize) {
        const batch = leadIds.slice(i, i + batchSize);
        const results = await Promise.all(
          batch.map(async (leadId) => {
            try {
              const activitySnap = await getDocs(
                query(
                  collection(db, 'leads', leadId, 'activities'),
                  orderBy('at', 'desc'),
                  limit(10)
                )
              );
              if (activitySnap.empty) return { leadId, summary: null };
              const activityRows = activitySnap.docs.map((docSnap) => docSnap.data() as any);
              const preferredRow =
                activityRows.find((row) => {
                  const type = String(row?.type || '').trim().toLowerCase();
                  return type === 'call' || type === 'visit';
                }) ||
                activityRows.find((row) => String(row?.type || '').trim().toLowerCase() === 'internal') ||
                null;
              if (preferredRow) {
                return {
                  leadId,
                  summary: {
                    label: formatActivityLabel(preferredRow),
                    at: preferredRow?.at || null,
                  } as LastActivitySummary,
                };
              }
              return { leadId, summary: null };
            } catch {
              return { leadId, summary: null };
            }
          })
        );

        if (cancelled) return;

        results.forEach((result) => {
          updates[result.leadId] = result.summary || { label: '—', at: null };
        });
      }

      if (cancelled || Object.keys(updates).length === 0) return;
      setLastActivityByLeadId((prev) => ({ ...prev, ...updates }));
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [lastActivityByLeadId, rows]);

  const selectedFilterLabel = selectedFilter?.label || 'All';
  const selectedStatusLabel = selectedStatusKeys.length === 0
    ? 'All statuses'
    : statusOptions
        .filter((option) => selectedStatusSet.has(option.key.toLowerCase()))
        .map((option) => option.label)
        .join(', ');
  const selectedRequiredTypeLabel =
    selectedRequiredType === 'all' ? 'All types' : selectedRequiredType;
  const showTypeColumn =
    reportMode === 'typeRequired' && selectedRequiredType === 'all';
  const tableColumnCount =
    reportMode === 'brokerAgency' ? 5 : (showTypeColumn ? 5 : 4);
  const selectedFilterCount = selectedFilter ? brokerAgencyCounts[selectedFilter.key] : undefined;
  const activityReady = rows.every((row) => Object.prototype.hasOwnProperty.call(lastActivityByLeadId, row.id));
  const bulkTestPassed = bulkRows.some((row) => row.status === 'test_sent');
  const bulkSelectedSet = useMemo(() => new Set(bulkSelectedKeys), [bulkSelectedKeys]);
  const selectedBulkRows = useMemo(
    () => bulkRows.filter((row) => bulkSelectedSet.has(row.filter.key)),
    [bulkRows, bulkSelectedSet]
  );
  const bulkAllSelected = bulkRows.length > 0 && bulkRows.every((row) => bulkSelectedSet.has(row.filter.key));
  const bulkSomeSelected = bulkRows.some((row) => bulkSelectedSet.has(row.filter.key));
  const bulkTemplateOptions = useMemo(
    () =>
      whatsappTemplates
        .filter((template) => String(template.headerFormat || '').trim().toUpperCase() === 'DOCUMENT')
        .sort((a, b) =>
          String(a.label || a.name || '').localeCompare(String(b.label || b.name || ''))
        ),
    [whatsappTemplates]
  );
  const selectedBulkTemplate = useMemo(
    () => bulkTemplateOptions.find((template) => String(template.id || '') === bulkTemplateId) || null,
    [bulkTemplateId, bulkTemplateOptions]
  );
  const periodLabel = periodMode === 'all'
    ? 'All'
    : `${formatDateTime(parseDateInput(fromDate, false))} to ${formatDateTime(parseDateInput(toDate, true))}`;
  const exportDisabled = exportPdfBusy || loading || !activityReady || !currentProjectId;

  useEffect(() => {
    if (bulkTemplateId && bulkTemplateOptions.some((template) => template.id === bulkTemplateId)) return;
    setBulkTemplateId(bulkTemplateOptions[0]?.id || '');
  }, [bulkTemplateId, bulkTemplateOptions]);

  const fetchActivitySummariesForRows = async (targetRows: LeadRow[]) => {
    const leadIds = targetRows
      .map((row) => String(row.id || '').trim())
      .filter(Boolean)
      .filter((id) => !lastActivityByLeadId[id]);
    if (leadIds.length === 0) return lastActivityByLeadId;

    const updates: Record<string, LastActivitySummary> = {};
    const batchSize = 25;
    for (let i = 0; i < leadIds.length; i += batchSize) {
      const batch = leadIds.slice(i, i + batchSize);
      const results = await Promise.all(
        batch.map(async (leadId) => {
          try {
            const activitySnap = await getDocs(
              query(
                collection(db, 'leads', leadId, 'activities'),
                orderBy('at', 'desc'),
                limit(10)
              )
            );
            if (activitySnap.empty) return { leadId, summary: null };
            const activityRows = activitySnap.docs.map((docSnap) => docSnap.data() as any);
            const preferredRow =
              activityRows.find((row) => {
                const type = String(row?.type || '').trim().toLowerCase();
                return type === 'call' || type === 'visit';
              }) ||
              activityRows.find((row) => String(row?.type || '').trim().toLowerCase() === 'internal') ||
              null;
            return {
              leadId,
              summary: preferredRow
                ? {
                    label: formatActivityLabel(preferredRow),
                    at: preferredRow?.at || null,
                  } as LastActivitySummary
                : null,
            };
          } catch {
            return { leadId, summary: null };
          }
        })
      );
      results.forEach((result) => {
        updates[result.leadId] = result.summary || { label: '—', at: null };
      });
    }

    const merged = { ...lastActivityByLeadId, ...updates };
    setLastActivityByLeadId((prev) => ({ ...prev, ...updates }));
    return merged;
  };

  const buildBrokerReportPdfBlob = async (
    reportRows: LeadRow[],
    filterLabel: string,
    fileNameBase: string,
    activityMap: Record<string, LastActivitySummary>,
    options?: { isAgencyHead?: boolean; agencyName?: string | null; headBrokerName?: string | null }
  ) => {
    const makeLeadRowsHtml = (rowsToRender: LeadRow[]) => rowsToRender
      .sort(
        (a, b) => toMillisLoose(b?.createdAt) - toMillisLoose(a?.createdAt)
      )
      .map((row, idx) => {
        const phoneDisplay = formatPhone(row);
        const phoneDigits = phoneDisplay.replace(/\D/g, '');
        const phoneHtml = phoneDigits
          ? `<a href="tel:${phoneDigits}" style="color:#0f172a;text-decoration:none;">${escapeHtml(phoneDisplay)}</a>`
          : escapeHtml(phoneDisplay);
        return `
          <tr>
            <td style="border:1px solid #cbd5e1;padding:6px 8px;text-align:center;">${idx + 1}</td>
            <td style="border:1px solid #cbd5e1;padding:6px 8px;">${escapeHtml(formatDateTime(row.createdAt))}</td>
            <td style="border:1px solid #cbd5e1;padding:6px 8px;font-weight:700;">${escapeHtml(formatLeadName(row.name))}</td>
            <td style="border:1px solid #cbd5e1;padding:6px 8px;">${phoneHtml}</td>
            <td style="border:1px solid #cbd5e1;padding:6px 8px;">${escapeHtml(String(row.projectName || '').trim() || '—')}</td>
            <td style="border:1px solid #cbd5e1;padding:6px 8px;">${escapeHtml(formatLastActivity(activityMap[row.id]))}</td>
          </tr>
        `;
      })
      .join('');
    const makeTableHtml = (rowsToRender: LeadRow[]) => `
      <table style="width:100%;border-collapse:collapse;font-size:12px;table-layout:fixed;line-height:1.7;">
        <colgroup>
          <col style="width:5%;" />
          <col style="width:12%;" />
          <col style="width:31%;" />
          <col style="width:16%;" />
          <col style="width:15%;" />
          <col style="width:21%;" />
        </colgroup>
        <thead>
          <tr style="background:#e2e8f0;">
            <th style="border:1px solid #cbd5e1;padding:7px 9px;">#</th>
            <th style="border:1px solid #cbd5e1;padding:7px 9px;">Date</th>
            <th style="border:1px solid #cbd5e1;padding:7px 9px;">Name</th>
            <th style="border:1px solid #cbd5e1;padding:7px 9px;">Phone</th>
            <th style="border:1px solid #cbd5e1;padding:7px 9px;">Project</th>
            <th style="border:1px solid #cbd5e1;padding:7px 9px;">Activity</th>
          </tr>
        </thead>
        <tbody>${makeLeadRowsHtml(rowsToRender) || '<tr><td colspan="6" style="border:1px solid #cbd5e1;padding:10px;text-align:center;">No rows found.</td></tr>'}</tbody>
      </table>
    `;
    const rowsForExport = [...reportRows].sort(
      (a, b) => toMillisLoose(b?.createdAt) - toMillisLoose(a?.createdAt)
    );
    const isAgencyHeadReport = options?.isAgencyHead === true;
    const groupedTablesHtml = isAgencyHeadReport
      ? Array.from(
          rowsForExport.reduce((map, row) => {
            const brokerName = String(row.referredBrokerName || '').trim() || 'Unassigned Broker';
            if (!map.has(brokerName)) map.set(brokerName, []);
            map.get(brokerName)?.push(row);
            return map;
          }, new Map<string, LeadRow[]>())
        )
          .sort((a, b) => {
            const headName = normalizeText(options?.headBrokerName);
            if (headName && normalizeText(a[0]) === headName) return -1;
            if (headName && normalizeText(b[0]) === headName) return 1;
            return b[1].length - a[1].length || a[0].localeCompare(b[0]);
          })
          .map(([brokerName, brokerRows]) => `
            <div style="margin-top:18px;">
              <div style="font-size:14px;font-weight:800;margin-bottom:6px;">
                ${escapeHtml(brokerName)} - ${brokerRows.length} lead${brokerRows.length === 1 ? '' : 's'}
              </div>
              ${makeTableHtml(brokerRows)}
            </div>
          `)
          .join('')
      : makeTableHtml(rowsForExport);
    const html = `
      <div style="font-family:Arial,Helvetica,sans-serif;color:#111827;line-height:1.7;">
        <div style="margin-bottom:14px;">
          <div style="font-size:21px;font-weight:800;letter-spacing:0.2px;">${isAgencyHeadReport ? 'Agency Lead Report' : 'Lead Report'}</div>
          <div style="display:flex;justify-content:space-between;gap:20px;margin-top:8px;font-size:13px;">
            <div>
              <div><strong>Broker:</strong> ${escapeHtml(filterLabel)}</div>
              ${isAgencyHeadReport ? `<div><strong>Agency:</strong> ${escapeHtml(String(options?.agencyName || '').trim() || '—')}</div>` : ''}
              <div><strong>Period:</strong> ${escapeHtml(periodLabel)}</div>
              <div><strong>Status:</strong> ${escapeHtml(selectedStatusLabel)}</div>
            </div>
            <div style="text-align:right;">
              <div><strong>Total Leads:</strong> ${reportRows.length}</div>
            </div>
          </div>
          ${isAgencyHeadReport ? `<div style="margin-top:10px;padding:8px 10px;background:#f1f5f9;border:1px solid #cbd5e1;border-radius:6px;font-size:12px;">This agency head report includes leads referred by brokers under ${escapeHtml(String(options?.agencyName || '').trim() || 'this agency')}.</div>` : ''}
        </div>
        ${groupedTablesHtml}
      </div>
    `;
    const payload = buildPdfPayloadFromTemplate({
      html,
      tokenValues: {},
      visualModel: {
        showTitle: false,
        showRefNo: false,
        showDate: false,
        showFooterContent: false,
        showPageNumbers: false,
        useCompanyFont: true,
        backgroundImageUrl: String(projectMeta?.backgroundImageUrl || ''),
        backgroundOpacity: 0.17,
        backgroundSize: 'contain',
        backgroundScale: 72,
        backgroundPosX: 50,
        backgroundPosY: 50,
        companyLogoHeightCm: 2.7,
        projectLogoHeightCm: 1.9,
      },
      companyLogoUrl: '',
      projectLogoUrl: String(projectMeta?.logoUrl || ''),
      companyAddress: String(companyMeta?.address || ''),
      companyMobile: String(companyMeta?.mobile || ''),
      companyEmail: String(companyMeta?.email || ''),
      companyFontFamily: String(companyMeta?.fontFamily || ''),
      companyFontUrl: String(companyMeta?.fontUrl || ''),
      companyFontFormat: String(companyMeta?.fontFormat || ''),
      gujaratiFontFamily: String(companyMeta?.gujaratiFontFamily || ''),
      gujaratiFontUrl: String(companyMeta?.gujaratiFontUrl || ''),
      gujaratiFontFormat: String(companyMeta?.gujaratiFontFormat || ''),
      paperSize: 'A4',
      fileName: fileNameBase,
    });
    const renderTemplatePdf = httpsCallable(getFunctions(), 'renderTemplatePdf');
    const result = await renderTemplatePdf(payload);
    return blobFromRenderTemplateResult(result.data);
  };

  const rowsForBrokerReport = (sourceRows: LeadRow[], filter: BrokerFilterOption) => {
    return sourceRows
      .filter((row) => matchesBrokerReportFilter(row, filter))
      .filter((row) => {
        if (selectedStatusSet.size === 0) return true;
        const raw = String(row.status || '').trim().toLowerCase();
        return !!raw && selectedStatusSet.has(raw);
      })
      .sort((a, b) => toMillisLoose(b?.createdAt) - toMillisLoose(a?.createdAt));
  };

  const prepareBulkBrokerReports = async () => {
    if (!currentProjectId) {
      setBulkError('Select a project first.');
      return;
    }
    if (reportMode !== 'brokerAgency') {
      setBulkError('Bulk broker reports are available only in Ref By mode.');
      return;
    }
    setBulkPreparing(true);
    setBulkError(null);
    try {
      const from = periodMode === 'range' ? parseDateInput(fromDate, false) : null;
      const to = periodMode === 'range' ? parseDateInput(toDate, true) : null;
      if (periodMode === 'range' && (!from || !to)) throw new Error('Select a valid from date and to date.');
      if (from && to && from.getTime() > to.getTime()) throw new Error('From date must be before To date.');
      const loaded = await loadLeadRows(from, to);
      const brokerOptions = filterOptions.filter((option) => option.type === 'broker');
      const nextRows = brokerOptions
        .map((filter) => ({
          filter,
          leadCount: rowsForBrokerReport(loaded, filter).length,
          status: 'ready' as BulkBrokerReportRow['status'],
        }))
        .filter((row) => row.leadCount > 0)
        .sort((a, b) => b.leadCount - a.leadCount || a.filter.label.localeCompare(b.filter.label));
      setBulkRows(nextRows);
      setBulkSelectedKeys(nextRows.map((row) => row.filter.key));
      setBulkTestBrokerKey((prev) => prev || nextRows[0]?.filter.key || '');
      setBulkDialogOpen(true);
    } catch (err: any) {
      setBulkError(err?.message || 'Failed to prepare broker reports.');
    } finally {
      setBulkPreparing(false);
    }
  };

  const updateBulkRow = (key: string, patch: Partial<BulkBrokerReportRow>) => {
    setBulkRows((prev) => prev.map((row) => (row.filter.key === key ? { ...row, ...patch } : row)));
  };

  const brokerReportTemplateValue = (
    row: BulkBrokerReportRow,
    sourceKey: string,
    reportRows: LeadRow[],
    fallback: string
  ) => {
    const key = String(sourceKey || '').trim().toLowerCase();
    const brokerName = String(row.filter.brokerName || row.filter.label || '').trim();
    const agencyName = String(row.filter.agencyName || '').trim();
    const valueMap: Record<string, string> = {
      broker_name: brokerName,
      customer_name: brokerName,
      name: brokerName,
      agency_name: agencyName,
      broker_agency: row.filter.label,
      lead_count: String(reportRows.length),
      leads_count: String(reportRows.length),
      total_leads: String(reportRows.length),
      period: periodLabel,
      date_range: periodLabel,
      report_period: periodLabel,
      company_name: String(companyMeta?.name || 'Swarna Group').trim(),
      project_name: String(projectMeta?.name || '').trim(),
      custom: '',
    };
    const value = String(valueMap[key] || fallback || '').trim() || '-';
    return ['broker_name', 'customer_name', 'name', 'agency_name', 'broker_agency'].includes(key)
      ? formatWhatsappPersonName(value)
      : value;
  };

  const buildBrokerReportTemplatePayload = (
    template: WhatsappTemplateOption,
    row: BulkBrokerReportRow,
    reportRows: LeadRow[],
    fileUrl: string,
    fileName: string
  ) => {
    const templateComponents: any[] = [{
      type: 'header',
      parameters: [{
        type: 'document',
        document: {
          link: fileUrl,
          filename: `${fileName}.pdf`,
        },
      }],
    }];
    const variables = Array.isArray(template.variables) ? template.variables : [];
    let resolvedText = String(template.bodyText || '').trim();

    if (variables.length) {
      const parameters = variables.map((entry, index) => {
        const value = brokerReportTemplateValue(
          row,
          String(entry?.source || entry?.key || ''),
          reportRows,
          String(entry?.defaultValue || '')
        );
        resolvedText = resolvedText.replace(`{{${index + 1}}}`, value);
        return { type: 'text', text: value };
      });
      templateComponents.push({ type: 'body', parameters });
    }

    const buttonVariables = Array.isArray(template.buttonVariables) ? template.buttonVariables : [];
    buttonVariables.forEach((entry) => {
      const value = brokerReportTemplateValue(
        row,
        String(entry?.source || entry?.key || ''),
        reportRows,
        String(entry?.defaultValue || '')
      );
      templateComponents.push({
        type: 'button',
        sub_type: 'url',
        index: String(entry?.index ?? '0'),
        parameters: [{ type: 'text', text: value }],
      });
    });

    return {
      templateComponents,
      resolvedText: resolvedText || null,
    };
  };

  const generateUploadAndSendBrokerReport = async (
    row: BulkBrokerReportRow,
    opts: { testPhone?: string }
  ) => {
    if (!selectedBulkTemplate) throw new Error('Select a WhatsApp template first.');
    if (String(selectedBulkTemplate.headerFormat || '').trim().toUpperCase() !== 'DOCUMENT') {
      throw new Error('Broker report template must use a document header.');
    }
    const brokerId = String(row.filter.brokerIds?.[0] || '').trim();
    if (!brokerId) throw new Error('Broker id missing.');
    const from = periodMode === 'range' ? parseDateInput(fromDate, false) : null;
    const to = periodMode === 'range' ? parseDateInput(toDate, true) : null;
    const loaded = await loadLeadRows(from, to);
    const reportRows = rowsForBrokerReport(loaded, row.filter);
    if (reportRows.length === 0) throw new Error('No leads found for this broker.');
    const activityMap = await fetchActivitySummariesForRows(reportRows);
    const filePeriodLabel = periodMode === 'all' ? 'All' : `${fromDate} to ${toDate}`;
    const fileNameBase = [
      row.filter.isAgencyHead ? 'Agency Lead Report' : 'Broker Lead Report',
      sanitizeFileNamePart(row.filter.label),
      sanitizeFileNamePart(filePeriodLabel),
    ].filter(Boolean).join(' - ');
    const blob = await buildBrokerReportPdfBlob(reportRows, row.filter.label, fileNameBase, activityMap, {
      isAgencyHead: row.filter.isAgencyHead,
      agencyName: row.filter.agencyName || null,
      headBrokerName: row.filter.brokerName || row.filter.label,
    });
    const storagePath = `broker-reports/${brokerId}/latest.pdf`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, await blobToUint8Array(blob), {
      contentType: 'application/pdf',
      cacheControl: 'no-store, max-age=0',
    });
    const baseUrl = await getDownloadURL(storageRef);
    const fileUrl = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}v=${Date.now()}`;
    const recipientTo = String(opts.testPhone || row.filter.phoneE164 || '').trim();
    if (!recipientTo) throw new Error('Broker WhatsApp number is missing.');
    const templatePayload = buildBrokerReportTemplatePayload(
      selectedBulkTemplate,
      row,
      reportRows,
      fileUrl,
      fileNameBase
    );
    const sendFn = httpsCallable(getFunctions(), 'whatsappSendMessage');
    await sendFn({
      to: recipientTo,
      templateName: String(selectedBulkTemplate.name || '').trim(),
      templateLanguage: String(selectedBulkTemplate.language || 'en').trim() || 'en',
      templateComponents: templatePayload.templateComponents,
      resolvedText: templatePayload.resolvedText,
      projectId: currentProjectId,
      metadata: {
        source: 'broker_report_bulk',
        brokerId,
        brokerName: row.filter.brokerName || row.filter.label,
        templateId: selectedBulkTemplate.id,
        templateName: selectedBulkTemplate.name || null,
        testMode: !!opts.testPhone,
        actualBrokerPhone: row.filter.phoneE164 || null,
        agencyHeadReport: row.filter.isAgencyHead === true,
        agencyName: row.filter.agencyName || null,
        sentTo: recipientTo,
        leadCount: reportRows.length,
        periodFrom: periodMode === 'range' ? fromDate : null,
        periodTo: periodMode === 'range' ? toDate : null,
      },
    });
    await setDoc(
      doc(db, 'brokers', brokerId),
      {
        lastReportGeneratedAt: Timestamp.now(),
        lastReportPeriodFrom: periodMode === 'range' ? fromDate : null,
        lastReportPeriodTo: periodMode === 'range' ? toDate : null,
        lastReportLeadCount: reportRows.length,
        lastReportUrl: fileUrl,
        lastReportStoragePath: storagePath,
        lastReportSentAt: Timestamp.now(),
        lastReportSendStatus: opts.testPhone ? 'test_sent' : 'sent',
      },
      { merge: true }
    );
  };

  const sendBulkTestReport = async () => {
    const target = bulkRows.find((row) => row.filter.key === bulkTestBrokerKey);
    const testPhone = String(bulkTestPhone || '').trim();
    if (!target) return setBulkError('Select one broker for test.');
    if (!testPhone) return setBulkError('Enter test WhatsApp number.');
    setBulkSending(true);
    setBulkError(null);
    updateBulkRow(target.filter.key, { status: 'testing', message: 'Sending test...' });
    try {
      await generateUploadAndSendBrokerReport(target, { testPhone });
      updateBulkRow(target.filter.key, { status: 'test_sent', message: `Test sent to ${testPhone}` });
    } catch (err: any) {
      updateBulkRow(target.filter.key, { status: 'failed', message: err?.message || 'Test failed.' });
      setBulkError(err?.message || 'Test failed.');
    } finally {
      setBulkSending(false);
    }
  };

  const sendAllBrokerReports = async () => {
    if (selectedBulkRows.length === 0) {
      setBulkError('Select at least one broker report to send.');
      return;
    }
    setBulkSending(true);
    setBulkError(null);
    try {
      for (const row of selectedBulkRows) {
        const phoneDigits = String(row.filter.phoneE164 || '').replace(/\D/g, '');
        if (phoneDigits.length < 10) {
          updateBulkRow(row.filter.key, { status: 'skipped', message: 'Broker WhatsApp number missing.' });
          continue;
        }
        updateBulkRow(row.filter.key, { status: 'sending', message: 'Sending...' });
        try {
          await generateUploadAndSendBrokerReport(row, {});
          updateBulkRow(row.filter.key, { status: 'sent', message: 'Sent' });
        } catch (err: any) {
          updateBulkRow(row.filter.key, { status: 'failed', message: err?.message || 'Failed' });
        }
      }
    } finally {
      setBulkSending(false);
    }
  };

  const handleExportPdf = async () => {
    if (exportDisabled) return;
    setExportPdfBusy(true);
    setExportPdfError(null);
    try {
      if (reportMode === 'brokerAgency' && selectedFilter?.type === 'broker' && selectedFilter.isAgencyHead) {
        const from = periodMode === 'range' ? parseDateInput(fromDate, false) : null;
        const to = periodMode === 'range' ? parseDateInput(toDate, true) : null;
        const loaded = await loadLeadRows(from, to);
        const reportRows = rowsForBrokerReport(loaded, selectedFilter);
        const activityMap = await fetchActivitySummariesForRows(reportRows);
        const filePeriodLabel = periodMode === 'all' ? 'All' : `${fromDate} to ${toDate}`;
        const fileNameBase = [
          'Agency Lead Report',
          sanitizeFileNamePart(selectedFilter.label),
          sanitizeFileNamePart(filePeriodLabel),
        ].filter(Boolean).join(' - ');
        const blob = await buildBrokerReportPdfBlob(reportRows, selectedFilter.label, fileNameBase, activityMap, {
          isAgencyHead: true,
          agencyName: selectedFilter.agencyName || null,
          headBrokerName: selectedFilter.brokerName || selectedFilter.label,
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${fileNameBase}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        return;
      }

      const rowsForExport = [...rows].sort(
        (a, b) => toMillisLoose(b?.createdAt) - toMillisLoose(a?.createdAt)
      );
      const filePeriodLabel = periodMode === 'all'
        ? 'All'
        : `${fromDate} to ${toDate}`;
      const fileNameBase = reportMode === 'typeRequired'
        ? [
          sanitizeFileNamePart(selectedRequiredTypeLabel),
          sanitizeFileNamePart(filePeriodLabel),
        ]
          .filter(Boolean)
          .join(' - ')
        : [
          'Lead Report',
          sanitizeFileNamePart(filePeriodLabel),
        ]
          .filter(Boolean)
          .join(' - ');
      const tableRowsHtml = rowsForExport
        .map((row, idx) => {
          const phoneDisplay = formatPhone(row);
          const phoneDigits = phoneDisplay.replace(/\D/g, '');
          const phoneHtml = phoneDigits
            ? `<a href="tel:${phoneDigits}" style="color:#0f172a;text-decoration:none;">${escapeHtml(phoneDisplay)}</a>`
            : escapeHtml(phoneDisplay);
          const projectCell = reportMode === 'brokerAgency'
            ? `<td style="border:1px solid #cbd5e1;padding:6px 8px;">${escapeHtml(String(row.projectName || '').trim() || '—')}</td>`
            : '';
          const typeCell = showTypeColumn
            ? `<td style="border:1px solid #cbd5e1;padding:6px 8px;">${escapeHtml(requiredTypeForLead(row) || '—')}</td>`
            : '';
          return `
            <tr>
              <td style="border:1px solid #cbd5e1;padding:6px 8px;text-align:center;">${idx + 1}</td>
              <td style="border:1px solid #cbd5e1;padding:6px 8px;">${escapeHtml(formatDateTime(row.createdAt))}</td>
              <td style="border:1px solid #cbd5e1;padding:6px 8px;font-weight:700;">${escapeHtml(formatLeadName(row.name))}</td>
              <td style="border:1px solid #cbd5e1;padding:6px 8px;">${phoneHtml}</td>
              ${projectCell}
              ${typeCell}
              <td style="border:1px solid #cbd5e1;padding:6px 8px;">${escapeHtml(formatLastActivity(lastActivityByLeadId[row.id]))}</td>
            </tr>
          `;
        })
        .join('');
      const html = `
        <div style="font-family:Arial,Helvetica,sans-serif;color:#111827;line-height:1.7;">
          <div style="margin-bottom:14px;">
            <div style="font-size:21px;font-weight:800;letter-spacing:0.2px;">${reportMode === 'typeRequired' ? 'Type Required' : 'Lead Report'}</div>
            <div style="display:flex;justify-content:space-between;gap:20px;margin-top:8px;font-size:13px;">
              <div>
                ${reportMode === 'brokerAgency' ? `<div><strong>Broker / Agency:</strong> ${escapeHtml(selectedFilterLabel)}</div>` : ''}
                ${(reportMode === 'typeRequired' && selectedRequiredType !== 'all') ? `<div><strong>Required Type:</strong> ${escapeHtml(selectedRequiredTypeLabel)}</div>` : ''}
                <div><strong>Period:</strong> ${escapeHtml(periodLabel)}</div>
              </div>
              <div style="text-align:right;">
                <div><strong>Total Leads:</strong> ${rows.length}</div>
              </div>
            </div>
          </div>
          <table style="width:100%;border-collapse:collapse;font-size:12px;table-layout:fixed;line-height:1.7;">
            <colgroup>
              <col style="width:5%;" />
              <col style="width:12%;" />
              <col style="width:${reportMode === 'brokerAgency' ? '31%' : (showTypeColumn ? '30%' : '36%')};" />
              <col style="width:${reportMode === 'brokerAgency' ? '16%' : (showTypeColumn ? '16%' : '20%')};" />
              ${reportMode === 'brokerAgency' ? '<col style="width:15%;" />' : ''}
              ${showTypeColumn ? '<col style="width:16%;" />' : ''}
              <col style="width:${reportMode === 'brokerAgency' ? '21%' : (showTypeColumn ? '21%' : '27%')};" />
            </colgroup>
            <thead>
              <tr style="background:#e2e8f0;">
                <th style="border:1px solid #cbd5e1;padding:7px 9px;">#</th>
                <th style="border:1px solid #cbd5e1;padding:7px 9px;">Date</th>
                <th style="border:1px solid #cbd5e1;padding:7px 9px;">Name</th>
                <th style="border:1px solid #cbd5e1;padding:7px 9px;">Phone</th>
                ${reportMode === 'brokerAgency' ? '<th style="border:1px solid #cbd5e1;padding:7px 9px;">Project</th>' : ''}
                ${showTypeColumn ? '<th style="border:1px solid #cbd5e1;padding:7px 9px;">Type</th>' : ''}
                <th style="border:1px solid #cbd5e1;padding:7px 9px;">Activity</th>
              </tr>
            </thead>
            <tbody>${tableRowsHtml || `<tr><td colspan="${reportMode === 'brokerAgency' ? '6' : (showTypeColumn ? '6' : '5')}" style="border:1px solid #cbd5e1;padding:10px;text-align:center;">No rows found.</td></tr>`}</tbody>
          </table>
        </div>
      `;
      const payload = buildPdfPayloadFromTemplate({
        html,
        tokenValues: {},
        visualModel: {
          showTitle: false,
          showRefNo: false,
          showDate: false,
          showFooterContent: false,
          showPageNumbers: false,
          useCompanyFont: true,
          backgroundImageUrl: String(projectMeta?.backgroundImageUrl || ''),
          backgroundOpacity: 0.17,
          backgroundSize: 'contain',
          backgroundScale: 72,
          backgroundPosX: 50,
          backgroundPosY: 50,
          companyLogoHeightCm: 2.7,
          projectLogoHeightCm: 1.9,
        },
        companyLogoUrl: '',
        projectLogoUrl: String(projectMeta?.logoUrl || ''),
        companyAddress: String(companyMeta?.address || ''),
        companyMobile: String(companyMeta?.mobile || ''),
        companyEmail: String(companyMeta?.email || ''),
        companyFontFamily: String(companyMeta?.fontFamily || ''),
        companyFontUrl: String(companyMeta?.fontUrl || ''),
        companyFontFormat: String(companyMeta?.fontFormat || ''),
        gujaratiFontFamily: String(companyMeta?.gujaratiFontFamily || ''),
        gujaratiFontUrl: String(companyMeta?.gujaratiFontUrl || ''),
        gujaratiFontFormat: String(companyMeta?.gujaratiFontFormat || ''),
        paperSize: 'A4',
        fileName: fileNameBase,
      });
      const functions = getFunctions();
      const renderTemplatePdf = httpsCallable(functions, 'renderTemplatePdf');
      const result = await renderTemplatePdf(payload);
      const blob = blobFromRenderTemplateResult(result.data);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileNameBase}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setExportPdfError(err?.message || 'Failed to export PDF.');
    } finally {
      setExportPdfBusy(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
        <BoxTitle />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {reportMode === 'typeRequired' ? 'Closed leads by required type' : 'Lead list by broker or agency'}
          </Typography>
          {reportMode === 'brokerAgency' ? (
            <Button
              variant="outlined"
              onClick={prepareBulkBrokerReports}
              disabled={bulkPreparing || loading || !currentProjectId}
            >
              {bulkPreparing ? 'Preparing...' : 'Bulk Send Broker Reports'}
            </Button>
          ) : null}
        </Stack>
      </Stack>

      <Paper sx={{ p: 1, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={reportMode}
          onChange={(_e, next) => {
            if (!next) return;
            setReportMode(next as ReportMode);
          }}
          sx={{
            gap: 1,
            '& .MuiToggleButton-root': {
              px: 2,
              py: 0.7,
              borderRadius: 999,
              borderColor: 'divider',
              textTransform: 'none',
              fontWeight: 700,
            },
            '& .Mui-selected': {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': { bgcolor: 'primary.dark' },
            },
          }}
        >
          <ToggleButton value="brokerAgency">Ref By</ToggleButton>
          <ToggleButton value="typeRequired">Type Required</ToggleButton>
        </ToggleButtonGroup>
      </Paper>

      {exportPdfError && <Alert severity="error">{exportPdfError}</Alert>}

      <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'flex-end' }}>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 180 } }}>
            <InputLabel id="report-period-mode-label">Period</InputLabel>
            <Select
              labelId="report-period-mode-label"
              label="Period"
              value={periodMode}
              onChange={(e) => setPeriodMode(e.target.value as 'all' | 'range')}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="range">Date Range</MenuItem>
            </Select>
          </FormControl>

          {periodMode === 'range' && (
            <TextField
              size="small"
              label="From"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          )}

          {periodMode === 'range' && (
            <TextField
              size="small"
              label="To"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          )}

          {reportMode === 'brokerAgency' ? (
            <Autocomplete
              sx={{ minWidth: { xs: '100%', md: 360 }, flex: 1 }}
              options={filterOptions}
              value={selectedFilter}
              onChange={(_, value) => setSelectedFilter(value)}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.key === value.key}
              filterOptions={(options, state) => {
                const queryText = normalizeSearchText(state.inputValue);
                if (!queryText) return options;
                const terms = queryText.split(/\s+/).filter(Boolean);
                return options.filter((option) => {
                  if (option.type === 'all') return false;
                  const haystack = option.searchText || normalizeSearchText(option.label);
                  return terms.every((term) => haystack.includes(term));
                });
              }}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <span>{option.label}</span>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                      {typeof brokerAgencyCounts[option.key] === 'number' ? brokerAgencyCounts[option.key] : '-'}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Broker / Agency"
                  placeholder="Select broker or agency"
                  size="small"
                />
              )}
            />
          ) : null}

          {reportMode === 'brokerAgency' ? (
            <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 260 } }}>
              <InputLabel id="report-status-filter-label">Status</InputLabel>
              <Select
                labelId="report-status-filter-label"
                label="Status"
                multiple
                value={selectedStatusKeys}
                onChange={(e) => setSelectedStatusKeys((e.target.value as string[]) || [])}
                renderValue={(selected) => {
                  const values = (selected as string[]) || [];
                  if (values.length === 0) return 'All statuses';
                  return statusOptions
                    .filter((option) => values.includes(option.key))
                    .map((option) => option.label)
                    .join(', ');
                }}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    <Checkbox checked={selectedStatusSet.has(option.key.toLowerCase())} size="small" />
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 260 } }}>
              <InputLabel id="report-required-type-label">Required Type</InputLabel>
              <Select
                labelId="report-required-type-label"
                label="Required Type"
                value={selectedRequiredType}
                onChange={(e) => setSelectedRequiredType(String(e.target.value || 'all'))}
              >
                <MenuItem value="all">All types</MenuItem>
                {requiredTypeOptions.map((typeValue) => (
                  <MenuItem key={typeValue} value={typeValue}>
                    {typeValue}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Button variant="contained" onClick={handleExportPdf} disabled={exportDisabled}>
            {exportPdfBusy ? 'Exporting...' : 'Export PDF'}
          </Button>

        </Stack>
      </Paper>

      {error && <Alert severity="error">{error}</Alert>}
      {bulkError && <Alert severity="error">{bulkError}</Alert>}

      <Paper sx={{ p: 0, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
          <Typography variant="h6" fontWeight={700}>
            {reportMode === 'typeRequired' ? 'Type Required' : 'Broker Lead List'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {reportMode === 'typeRequired'
              ? `Required Type: ${selectedRequiredTypeLabel} | Rows: ${rows.length}`
              : `Filter: ${selectedFilterLabel}${typeof selectedFilterCount === 'number' ? ` (${selectedFilterCount})` : ''} | Status: ${selectedStatusLabel} | Rows: ${rows.length}`}
          </Typography>
        </Stack>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Phone</TableCell>
                {reportMode === 'brokerAgency' && <TableCell>Project</TableCell>}
                {showTypeColumn && <TableCell>Type</TableCell>}
                <TableCell>Activity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={tableColumnCount} align="center">
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ py: 2 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2">Generating report…</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}

              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={tableColumnCount} align="center">
                    No leads found for the selected filter.
                  </TableCell>
                </TableRow>
              )}

              {!loading && rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                  <TableCell>{formatLeadName(row.name)}</TableCell>
                  <TableCell>{formatPhone(row)}</TableCell>
                  {reportMode === 'brokerAgency' && <TableCell>{String(row.projectName || '').trim() || '—'}</TableCell>}
                  {showTypeColumn && <TableCell>{requiredTypeForLead(row) || '—'}</TableCell>}
                  <TableCell>{formatLastActivity(lastActivityByLeadId[row.id])}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={bulkDialogOpen} onClose={() => (bulkSending ? undefined : setBulkDialogOpen(false))} fullWidth maxWidth="lg">
        <DialogTitle>Bulk Send Broker Reports</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <Alert severity="info">
              Test one broker report first. PDFs overwrite each broker&apos;s latest report file.
            </Alert>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                <Typography variant="body2">
                  <strong>Lead date range:</strong> {periodLabel}
                </Typography>
                <Typography variant="body2">
                  <strong>Status:</strong> {selectedStatusLabel}
                </Typography>
                <Typography variant="body2">
                  <strong>Selected:</strong> {selectedBulkRows.length} of {bulkRows.length}
                </Typography>
              </Stack>
            </Paper>
            <FormControl size="small" fullWidth>
              <InputLabel id="bulk-template-label">WhatsApp Template</InputLabel>
              <Select
                labelId="bulk-template-label"
                label="WhatsApp Template"
                value={bulkTemplateId}
                onChange={(e) => setBulkTemplateId(String(e.target.value || ''))}
                disabled={bulkSending || whatsappTemplatesLoading}
              >
                {bulkTemplateOptions.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {String(template.label || template.name || template.id)}
                    {template.name ? ` (${template.name})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {!whatsappTemplatesLoading && bulkTemplateOptions.length === 0 ? (
              <Alert severity="warning">
                No document-header WhatsApp template found. Create/fetch an approved Meta template in WhatsApp Settings, set Header Format as Document, then select it here.
              </Alert>
            ) : null}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
              <TextField
                label="Test WhatsApp Number"
                value={bulkTestPhone}
                onChange={(e) => setBulkTestPhone(e.target.value)}
                size="small"
                fullWidth
                placeholder="+91..."
              />
              <FormControl size="small" fullWidth>
                <InputLabel id="bulk-test-broker-label">Test Broker</InputLabel>
                <Select
                  labelId="bulk-test-broker-label"
                  label="Test Broker"
                  value={bulkTestBrokerKey}
                  onChange={(e) => setBulkTestBrokerKey(String(e.target.value || ''))}
                >
                  {bulkRows.map((row) => (
                    <MenuItem key={row.filter.key} value={row.filter.key}>
                      {row.filter.label} ({row.leadCount})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="contained" onClick={sendBulkTestReport} disabled={bulkSending || bulkRows.length === 0 || !selectedBulkTemplate}>
                Send Test
              </Button>
            </Stack>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={bulkAllSelected}
                        indeterminate={bulkSomeSelected && !bulkAllSelected}
                        disabled={bulkSending || bulkRows.length === 0}
                        onChange={(e) => {
                          setBulkSelectedKeys(e.target.checked ? bulkRows.map((row) => row.filter.key) : []);
                        }}
                        inputProps={{ 'aria-label': 'Select all broker reports' }}
                      />
                    </TableCell>
                    <TableCell>Broker</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell align="right">Leads</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bulkRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">No broker reports found for this criteria.</TableCell>
                    </TableRow>
                  ) : bulkRows.map((row) => (
                    <TableRow key={row.filter.key}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={bulkSelectedSet.has(row.filter.key)}
                          disabled={bulkSending}
                          onChange={(e) => {
                            setBulkSelectedKeys((prev) => {
                              if (e.target.checked) return Array.from(new Set([...prev, row.filter.key]));
                              return prev.filter((key) => key !== row.filter.key);
                            });
                          }}
                          inputProps={{ 'aria-label': `Select ${row.filter.label}` }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                          <Typography variant="body2">{row.filter.label}</Typography>
                          {row.filter.isAgencyHead ? (
                            <Chip size="small" color="primary" variant="outlined" label="Agency Head Report" />
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell>{row.filter.phoneE164 || '—'}</TableCell>
                      <TableCell align="right">{row.leadCount}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {row.status.replace(/_/g, ' ')}
                        </Typography>
                        {row.message ? (
                          <Typography variant="caption" color="text.secondary">
                            {row.message}
                          </Typography>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialogOpen(false)} disabled={bulkSending}>Close</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={sendAllBrokerReports}
            disabled={bulkSending || selectedBulkRows.length === 0 || !bulkTestPassed || !selectedBulkTemplate}
          >
            {bulkSending ? 'Sending...' : `Send Selected (${selectedBulkRows.length})`}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function BoxTitle() {
  return (
    <Typography variant="h5" fontWeight={700}>
      Reports
    </Typography>
  );
}
